import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useDebounceValue } from 'usehooks-ts';
import { ChevronDown, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_PAGE_SIZE } from '@/constants/pagination';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Pagination } from '@/components/ui/pagination';
import { useInvoices } from '@/hooks/useInvoices';
// TEMP: Bulk Update hidden — uncomment to restore
// import { useBulkUpdateInvoiceStatus } from '@/hooks/useInvoices';
import type { CustomerInvoice } from '@/apis/invoices';
// TEMP: Bulk Update hidden — uncomment to restore
// import type { CustomerInvoiceStatus } from '@/apis/invoices';
import { InvoiceKpiCards } from './InvoiceKpiCards';
import { InvoiceToolbar } from './InvoiceToolbar';
import type { InvoiceFilters } from './InvoiceFilterPopover';
import { InvoiceTable, type InvoiceRowAction } from './InvoiceTable';
import { NewInvoiceMenu, type NewInvoiceKind } from './NewInvoiceMenu';
import { ComingSoonDialog } from './ComingSoonDialog';
// TEMP: Bulk Update hidden — uncomment to restore
// import { InvoiceBulkUpdateDialog } from './InvoiceBulkUpdateDialog';
import { DeleteInvoiceDialog } from './DeleteInvoiceDialog';
import { RecordPaymentDialog } from './RecordPaymentDialog';
import { InvoiceExportDialog } from './InvoiceExportDialog';
import { downloadInvoicesCsv } from './invoice-shared';

const headerLightBtn =
  'flex h-10 items-center gap-2 rounded-full bg-primary-s50 px-5 text-sm font-medium text-primary-s500 hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50';
const cardClass = 'rounded-2xl border border-smoke bg-neutral-white p-6';

export function InvoicePage() {
  const search = useSearch({ from: '/_admin/invoice/' });
  const navigate = useNavigate({ from: '/invoice/' });

  const page = search.page ?? 1;
  const limit = search.limit ?? DEFAULT_PAGE_SIZE;

  const [searchInput, setSearchInput] = useState(search.search ?? '');
  const [debouncedSearch] = useDebounceValue(searchInput, 400);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Every list-affecting change goes through here — stale selections would
  // otherwise survive page/filter switches and hit unintended invoices.
  const setParams = (patch: Record<string, unknown>) => {
    setSelected(new Set());
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };

  const filters: InvoiceFilters = {
    status: search.status,
    startDate: search.startDate,
    endDate: search.endDate,
  };

  const documentType = search.documentType ?? 'invoice';
  const isCreditMemo = documentType === 'credit_memo';

  const { data, isLoading } = useInvoices({
    page,
    limit,
    search: debouncedSearch || undefined,
    documentType,
    ...filters,
  });
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  // TEMP: Bulk Update hidden — uncomment to restore
  // const bulkUpdate = useBulkUpdateInvoiceStatus();

  const [deleteTarget, setDeleteTarget] = useState<CustomerInvoice | null>(null);
  const [paymentTarget, setPaymentTarget] = useState<CustomerInvoice | null>(
    null,
  );
  const [comingSoon, setComingSoon] = useState<string | null>(null);
  // const [bulkOpen, setBulkOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => {
    const pageIds = rows.map((r) => r.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
    setSelected(allSelected ? new Set() : new Set(pageIds));
  };

  // TEMP: Bulk Update hidden — uncomment to restore
  // const onBulkApply = (status: CustomerInvoiceStatus) =>
  //   bulkUpdate.mutate(
  //     { ids: [...selected], status },
  //     {
  //       onSuccess: () => {
  //         setBulkOpen(false);
  //         setSelected(new Set());
  //       },
  //     },
  //   );


  const onRowAction = (action: InvoiceRowAction, inv: CustomerInvoice) => {
    // A credit-memo row must land on the credit-memo routes, or it opens in the
    // invoice form and loses its credit fields.
    const isCredit = inv.documentType === 'credit_memo';
    if (action === 'view') {
      void navigate({
        to: isCredit ? '/invoice/credit-memo/$invoiceId' : '/invoice/$invoiceId',
        params: { invoiceId: inv.id },
      });
    } else if (action === 'edit') {
      void navigate({
        to: isCredit
          ? '/invoice/credit-memo/$invoiceId/edit'
          : '/invoice/$invoiceId/edit',
        params: { invoiceId: inv.id },
      });
    } else if (action === 'payment') {
      setPaymentTarget(inv);
    } else {
      setDeleteTarget(inv);
    }
  };

  const onNew = (kind: NewInvoiceKind) => {
    if (kind === 'invoice') void navigate({ to: '/invoice/new' });
    else if (kind === 'credit-note')
      void navigate({ to: '/invoice/credit-memo/new' });
    else setComingSoon('New Recurring Invoice');
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-black">Invoice</h1>
        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className={headerLightBtn}>
                Export <ChevronDown size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuItem
                className="justify-center bg-primary-s500 font-medium text-white focus:bg-primary-s500 focus:text-white"
                onClick={() => setExportOpen(true)}
              >
                Export Invoices
              </DropdownMenuItem>
              <DropdownMenuItem
                className="justify-center"
                onClick={() => {
                  // Current view = the rows on screen (page + search + filters)
                  if (!rows.length) {
                    toast.info('No invoices to export');
                    return;
                  }
                  downloadInvoicesCsv(rows, `invoices-${new Date().toISOString().slice(0, 10)}.csv`);
                }}
              >
                Export current view
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Disabled until the bulk invoice import ships */}
          <button
            type="button"
            disabled
            onClick={() => setComingSoon('Upload Invoices')}
            className={headerLightBtn}
          >
            <Upload size={16} /> Upload
          </button>
          <NewInvoiceMenu onSelect={onNew} />
        </div>
      </div>

      <div className="inline-flex rounded-full border border-smoke bg-neutral-white p-1">
        {(
          [
            { value: 'invoice', label: 'Invoices' },
            { value: 'credit_memo', label: 'Credit Memos' },
          ] as const
        ).map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() =>
              void navigate({
                to: '/invoice',
                // Page and status are dropped on purpose: the two vocabularies
                // do not overlap, so a carried-over status filter shows nothing.
                search: { page: 1, documentType: tab.value },
              })
            }
            className={`h-9 rounded-full px-5 text-sm font-medium transition-colors ${
              documentType === tab.value
                ? 'bg-primary-s500 text-white'
                : 'text-lightGray hover:text-black'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* AR KPIs exclude credit memos on the backend, so showing them above a
          credit-memo list would misstate the numbers. */}
      {!isCreditMemo && (
        <div className={cardClass}>
          <InvoiceKpiCards />
        </div>
      )}

      <div className={`${cardClass} flex flex-col gap-6`}>
      <InvoiceToolbar
        searchValue={searchInput}
        onSearchChange={(v) => {
          setSearchInput(v);
          setParams({ search: v || undefined, page: 1 });
        }}
        filters={filters}
        onFiltersChange={(f) =>
          // setParams merges into the previous search, so every filter key
          // must be explicitly unset first or "Clear filters" is a no-op.
          setParams({
            status: undefined,
            startDate: undefined,
            endDate: undefined,
            ...f,
            page: 1,
          })
        }
        // TEMP: Bulk Update hidden — uncomment to restore
        // selectedCount={selected.size}
        // onBulkUpdate={() => setBulkOpen(true)}
      />

      {/* Stable height for a full page of rows — result-count changes from
          search/filter must not make the section jump */}
      <div className="min-h-[610px]">
        <InvoiceTable
          rows={rows}
          isLoading={isLoading}
          onRowAction={onRowAction}
          selectedIds={selected}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
        />
      </div>

      {total > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          hasNext={page < totalPages}
          hasPrev={page > 1}
          onPageChange={(p) => setParams({ page: p })}
          onLimitChange={(l) => setParams({ limit: l, page: 1 })}
          itemLabel="invoices"
        />
      )}
      </div>

      <ComingSoonDialog
        open={!!comingSoon}
        onOpenChange={(o) => {
          if (!o) setComingSoon(null);
        }}
        title={comingSoon ?? ''}
      />

      {/* TEMP: Bulk Update hidden — uncomment to restore
      <InvoiceBulkUpdateDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        count={selected.size}
        pending={bulkUpdate.isPending}
        onApply={onBulkApply}
      />
      */}

      <DeleteInvoiceDialog invoice={deleteTarget} onClose={() => setDeleteTarget(null)} />
      <RecordPaymentDialog invoice={paymentTarget} onClose={() => setPaymentTarget(null)} />

      <InvoiceExportDialog open={exportOpen} onOpenChange={setExportOpen} />
    </div>
  );
}

export default InvoicePage;
